import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Heart, MailOpen, Send, Shield, Sparkles, Users } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MembershipStatusCard } from "@/components/membership-card";

export function DashboardSummary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: () => api("/dashboard") });
  if (!data) return <div className="h-48 animate-pulse rounded-[20px] bg-[#F4EBE6]" />;
  const verified = data.verification_status === "verified";
  const cards = [
    { k: "matches", label: "Suitable matches", value: data.suitable_matches, Icon: Heart, to: "/discover" },
    { k: "incoming", label: "Pending incoming", value: data.pending_incoming, Icon: MailOpen, to: "/requests" },
    { k: "outgoing", label: "Pending outgoing", value: data.pending_outgoing, Icon: Send, to: "/requests" },
    { k: "connections", label: "Connections", value: data.connections, Icon: Users, to: "/connections" },
  ];
  return (
    <div data-testid="dashboard-summary" className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <button key={c.k} data-testid={`dashboard-card-${c.k}`} onClick={() => navigate(c.to)} className="rounded-[20px] border border-border bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md">
            <c.Icon className="h-[18px] w-[18px] text-primary" />
            <span className="mt-2 block font-display text-[32px] font-bold leading-none" data-testid={`dashboard-value-${c.k}`}>{c.value}</span>
            <span className="mt-1 block text-xs text-muted-foreground">{c.label}</span>
          </button>
        ))}
      </div>
      <MembershipStatusCard membership={user?.membership} onClick={() => navigate("/membership")} />
      <div data-testid="dashboard-verification" className={`flex items-center gap-3 rounded-[20px] border bg-white p-4 ${verified ? "border-[#C19B53]" : "border-border"}`}>
        {verified ? <CheckCircle2 className="h-5 w-5 text-[#C19B53]" /> : <Shield className="h-5 w-5 text-muted-foreground" />}
        <span className="flex-1 text-[15px] font-bold">{verified ? "Verified Member" : "Unverified"}</span>
        <button data-testid="dashboard-get-verified-button" onClick={() => navigate("/verification")} className="h-10 rounded-full bg-[#C19B53] px-4 text-sm font-bold text-white transition-opacity hover:opacity-90">
          {verified ? "View status" : "Get Verified"}
        </button>
      </div>
      <div data-testid="dashboard-completion" className="rounded-[20px] border border-border bg-white p-4">
        <div className="flex items-center justify-between">
          <span className="text-[15px] font-bold">Profile completion</span>
          <span className="font-display text-2xl font-bold text-primary" data-testid="dashboard-completion-pct">{data.completion}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#F4EBE6]" role="progressbar" aria-valuenow={data.completion} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${data.completion}%` }} />
        </div>
        {data.suggestions.slice(0, 3).map((s) => (
          <p key={s} className="mt-2 flex items-center gap-1.5 text-xs"><Sparkles className="h-3.5 w-3.5 text-primary" />{s}</p>
        ))}
      </div>
    </div>
  );
}
